import React, { useState, useEffect } from 'react';
import { Shield, MapPin, Activity, FileWarning, Check, X, AlertTriangle, Clock } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '../../../api/core.js';

// Fallback notice content when no privacy_notice row of type=driver is
// active for the tenant. Mirrors the salon mockup "Cédric" scene so the
// demo and the real onboarding tell the same story.
const FALLBACK_NOTICE = {
  title: 'Notice RGPD conducteur — données collectées dans la flotte',
  summary: 'Avant ta première mission, voici ce qui sera collecté sur toi, pourquoi, et combien de temps c\'est gardé.',
  content: `Conformément aux articles 13 et 14 du RGPD, tu es informé(e) que ton employeur collectera les données suivantes pendant ta présence dans la flotte de l'entreprise.

CATÉGORIES DE DONNÉES ET FINALITÉS
- Géolocalisation du véhicule pendant tes heures de travail (sécurité, suivi mission, facturation client). Mode "hors heures travail" activé par défaut : le boîtier coupe à 18h vendredi et reprend à 7h lundi.
- Scores éco-conduite (accélération, freinage, vitesse) pour la maintenance préventive du véhicule.
- Dossiers sinistre, constats, photos, échanges avec courtier (obligation légale assurance).

DURÉES DE CONSERVATION
- Géolocalisation : 90 jours, puis purge automatique.
- Scores éco-conduite : 365 jours, puis purge automatique.
- Dossiers sinistre : 5 ans (durée de prescription civile), puis purge automatique.

TES DROITS RGPD (toujours actifs en 1 clic depuis ton app conducteur)
- Droit d'accès (art. 15) : récupérer toutes tes données dans un PDF.
- Droit de rectification (art. 16) : corriger une donnée erronée.
- Droit à l'effacement (art. 17) : demander la suppression de tes données (sauf rétention légale en cours).
- Droit à la portabilité (art. 20) : exporter tes données vers un autre employeur.

Délai de réponse à toute demande RGPD : 30 jours maximum (légal). FleetManager Max répond en pratique sous quelques minutes pour les exports automatisés.

REFUS DE SIGNATURE
Tu peux reporter ta signature, mais sans cet accord ton affectation à un véhicule sera bloquée — c'est la condition technique d'usage du parc, pas un consentement marketing optionnel.`,
};

const CATEGORIES = [
  {
    key: 'geoloc',
    icon: MapPin,
    color: 'blue',
    title: 'Géolocalisation',
    finality: 'Sécurité & facturation client · heures travail uniquement par défaut',
    retention: '90 jours',
  },
  {
    key: 'scoring',
    icon: Activity,
    color: 'emerald',
    title: 'Scores éco-conduite',
    finality: 'Maintenance préventive véhicule · accélération, freinage, vitesse',
    retention: '365 jours',
  },
  {
    key: 'claims',
    icon: FileWarning,
    color: 'amber',
    title: 'Dossiers sinistre',
    finality: 'Constats, photos, courtier · obligation légale assurance',
    retention: '5 ans (légal)',
  },
];

const COLOR_CLASSES = {
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', title: 'text-blue-900', text: 'text-blue-800', retention: 'text-blue-700' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'text-emerald-600', title: 'text-emerald-900', text: 'text-emerald-800', retention: 'text-emerald-700' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600', title: 'text-amber-900', text: 'text-amber-800', retention: 'text-amber-700' },
};

function PrivacyNoticeModal({ isOpen, driver, onAcknowledged, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [showRefuseWarning, setShowRefuseWarning] = useState(false);

  // Fetch the active driver-type privacy notice for this tenant
  const { data: noticesData, isLoading } = useQuery({
    queryKey: ['privacy_notice_driver_active'],
    queryFn: () => apiRequest('/privacy_notice?notice_type=driver&is_active=1&_limit=1'),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  // Reset transient state when the modal opens
  useEffect(() => {
    if (isOpen) {
      setSubmitting(false);
      setErrorMsg(null);
      setShowRefuseWarning(false);
    }
  }, [isOpen]);

  if (!isOpen || !driver) return null;

  const notice = noticesData?.data?.[0] || FALLBACK_NOTICE;
  const firstName = driver.first_name || 'Conducteur';
  const driverId = driver.id;

  const handleAcknowledge = async () => {
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const payload = {
        driver_id: driverId,
        notice_id: notice.id || null,
        device_info: navigator.userAgent || 'unknown',
      };
      const result = await apiRequest('/privacy_notice_acknowledgment', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      onAcknowledged && onAcknowledged(result);
    } catch (err) {
      console.error('[PrivacyNoticeModal] Acknowledge failed:', err);
      setErrorMsg(`Impossible d'enregistrer la signature: ${err.message || err}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefuseClick = () => {
    if (!showRefuseWarning) {
      setShowRefuseWarning(true);
      return;
    }
    onClose && onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 rounded-t-2xl flex items-start justify-between">
          <div className="flex items-start gap-3">
            <Shield className="w-7 h-7 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-lg font-bold">Bienvenue {firstName} — notice RGPD à signer</div>
              <div className="text-blue-100 text-xs mt-0.5">Avant ta première mission · Art. 13 RGPD · acknowledgment tracé</div>
            </div>
          </div>
          <button
            onClick={handleRefuseClick}
            className="text-blue-100 hover:text-white transition-colors"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          {isLoading ? (
            <div className="text-sm text-gray-500">Chargement de la notice...</div>
          ) : (
            <>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">{notice.title}</h3>
                {notice.summary && (
                  <p className="text-sm text-gray-600">{notice.summary}</p>
                )}
              </div>

              {/* 3 category cards */}
              <div className="grid grid-cols-3 gap-3">
                {CATEGORIES.map((cat) => {
                  const c = COLOR_CLASSES[cat.color];
                  const Icon = cat.icon;
                  return (
                    <div key={cat.key} className={`${c.bg} border ${c.border} rounded-lg p-3`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <Icon className={`w-4 h-4 ${c.icon}`} />
                        <span className={`font-semibold text-sm ${c.title}`}>{cat.title}</span>
                      </div>
                      <div className={`text-[11px] ${c.text} leading-snug`}>{cat.finality}</div>
                      <div className={`mt-1.5 inline-flex items-center gap-1 text-[10px] ${c.retention} font-medium`}>
                        <Clock className="w-3 h-3" />
                        Conservation : {cat.retention}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Notice content */}
              {notice.content && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{notice.content}</pre>
                </div>
              )}

              {/* Conformity trace info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
                <span className="font-semibold">Trace conformité :</span> ta signature sera horodatée et associée à ton numéro de conducteur. Acknowledgment archivé dans le registre RGPD de ton entreprise pour audit éventuel.
              </div>

              {errorMsg && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  {errorMsg}
                </div>
              )}

              {showRefuseWarning && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-900">
                  <div className="font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Reporter la signature ?</div>
                  <div className="mt-1 text-xs text-amber-800">
                    Sans cet accord, {firstName} ne pourra pas être affecté(e) à un véhicule de la flotte. La signature est une condition technique d'usage, pas un consentement marketing optionnel.
                    Clique à nouveau sur fermer pour confirmer le report, ou signe la notice maintenant.
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between bg-gray-50 rounded-b-2xl">
          <div className="text-xs text-gray-500">
            Notice version <span className="font-mono">{notice.version || '1.0'}</span> · langue <span className="font-mono">{notice.language || 'fr'}</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleRefuseClick}
              disabled={submitting}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-white disabled:opacity-50"
            >
              {showRefuseWarning ? 'Confirmer le report' : 'Reporter'}
            </button>
            <button
              onClick={handleAcknowledge}
              disabled={submitting || isLoading}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
            >
              <Check className="w-4 h-4" />
              {submitting ? 'Enregistrement...' : 'Signer la notice'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyNoticeModal;
